import React from "react";
import styled from "styled-components";

import { FlexJustify } from "common/fragments/Flex";
import { CheckBoxView } from "common/views/checkbox/Checkbox.view";
import MultimediaView, { PlayerRef } from "common/views/multimedia/Multimedia.view";
import { ITab, TabsView } from "common/views/tab/Tabs.view";
import Label from "core/components/label/Label";
import BrowserUtil from "core/util/BrowserUtil";
import JsxUtil from "core/util/JsxUtil";
import XhrUtil from "core/util/XhrUtil";


interface ICompositeMultimediaProps {
	src: string;
	poster?: string; // uri for placeholder poster image
	transcript?: string; // uri for transcript file
	audioMode?: boolean;
	aspectratio?: string;
	tabs?: ITab[]; // additional tab
	viewKeyPrefix: string;
}

interface ITranscriptNode {
	start: number;
	text: string;
}

interface ICompositeMultimediaState {
	transcript: ITranscriptNode[];
	active: number;
	cycle: number;
	query: string;
	autoScroll: boolean;
}

const TranscriptWrapper = styled.div`

	max-height: 400px;
	padding: 15px;
	overflow-y: scroll;

	& a {
		color: inherit;
		text-decoration: none;
	}

	& a:hover,
	& a.active {
		background-color: rgba(19, 124, 189, 0.15);
		border-bottom: 1px solid rgba(19, 124, 189, 0.15);
	}

	& .match {
		background-color: #db3737;
		color: #fff;
	}
`;

export default class CompositeMultimedia extends React.Component<ICompositeMultimediaProps, ICompositeMultimediaState> {

	private playerRef: PlayerRef;
	private transcriptRef: React.RefObject<HTMLDivElement>;
	private activeRef: React.RefObject<HTMLAnchorElement>;

	public constructor(props: ICompositeMultimediaProps) {
		super(props);
		this.state = {
			transcript: [],
			active: -1,
			cycle: -1,
			query: '',
			autoScroll: true
		};

		this.playerRef = React.createRef();
		this.transcriptRef = React.createRef();
		this.activeRef = React.createRef();
	}

	public render() {

		const { aspectratio, audioMode, poster, src, tabs, viewKeyPrefix } = this.props;
		const { query, transcript } = this.state;

		let tabList: ITab[] = [];

		if (transcript.length) {
			const nodes = [];
			const queryLowerCase = query ? query.toLowerCase() : "";

			for (let i = 0, len = transcript.length; i < len; i++) {
				if (i > 0) {
					nodes.push(' ');
				}

				const node = transcript[i];
				let text: React.ReactNode = node.text || "";

				if (queryLowerCase && node.text && node.text.toLowerCase().includes(queryLowerCase)) {
					text = JsxUtil.highlightText(node.text, queryLowerCase);
				}

				const nodeProps: any = {
					'key': i + '.' + node.start,
					'onClick': this.onClickNode,
					'data-start': node.start
				};

				if (i === this.state.active) {
					nodeProps.className = 'active';
					nodeProps.ref = this.activeRef;
				}

				nodes.push(
					<a {...nodeProps}>
						{text}
					</a>
				);
			}

			tabList.push({
				key: '__transcript',
				label: <Label k="media.transcript" v="Transcript" />,
				content: (
					<div>
						<FlexJustify>
							<div />
							<div>
								<CheckBoxView
									viewKey={viewKeyPrefix + '.transcript.autoscroll'}
									value={this.state.autoScroll}
									onChange={this.handleAutoScrollToggle}
									label={<Label k="media.transcript.autoscroll" v="Enable Auto Scroll" />}
								/>
							</div>
						</FlexJustify>
						<TranscriptWrapper ref={this.transcriptRef}>
							{nodes}
						</TranscriptWrapper>
					</div>
				)
			});
		}

		if (tabs && tabs.length) {
			tabList = [...tabList, ...tabs];
		}

		return (
			<div>
				<MultimediaView
					viewKey={viewKeyPrefix + '.media'}
					src={src}
					poster={poster}
					onTimeUpdate={this.onTimeUpdate}
					playerRef={this.playerRef}
					audioMode={audioMode}
					aspectratio={aspectratio}
				/>
				<TabsView viewKey={viewKeyPrefix + '.tabs'} tabs={tabList} />
			</div>
		);
	}

	public componentDidMount() {
		this.loadTranscript();
	}

	public componentDidUpdate(prevProps: ICompositeMultimediaProps, prevState: ICompositeMultimediaState) {

		// if a new transcript file url is provided, load it
		if (this.props.transcript && this.props.transcript !== prevProps.transcript) {
			this.loadTranscript();
		}

		if (this.state.autoScroll) {
			// move current scroll pos of transcript if current node has changed
			const transcriptNode = this.transcriptRef.current;
			const activeNode = this.activeRef.current;
			if (transcriptNode && activeNode && prevState.active !== this.state.active) {
				BrowserUtil.scrollContainer(activeNode, transcriptNode, 50);
			}
		}
	}

	private onClickNode = (e: any) => {
		const start = parseInt(e.target.getAttribute('data-start'), 10);
		if (!isNaN(start)) {
			const { current } = this.playerRef;
			if (current) {
				current.seek(start);
			}
		}
	}

	private handleAutoScrollToggle = (autoScroll: boolean) => this.setState({autoScroll});

	private onTimeUpdate = (o: {duration: number; position: number}) => {
		const { active, transcript } = this.state;
		if (!transcript.length) {
			return;
		}

		let nextActive = active;

		for (let i = 0, len = transcript.length; i < len; i++) {
			const n1 = transcript[i];
			const n2 = transcript[i + 1];
			if (!n2) {
				// last item must be active if we ended up getting here
				nextActive = i;
				break;
			}

			if (o.position >= n1.start && o.position < n2.start) {
				nextActive = i;
				break;
			}
		}

		if (nextActive !== active) {
			this.setState({active: nextActive});
		}
	}

	private loadTranscript = () => {

		if (this.props.transcript) {
			XhrUtil.getXml(this.props.transcript).then((doc) => {
				if (doc) {
					const nodes = doc.querySelectorAll('body div p');
					if (!nodes) {
						return;
					}

					const transcript = [];

					for (let i = 0, len = nodes.length; i < len; i++) {
						const n = nodes[i];

						const attr = n.getAttribute('begin');

						if (attr) {
							const begin = attr.split(':');
							let i1: number;
							let pos: number = 0;
							let factor: number = 1.0;

							for (i1 = 2; i1 >= 0; i1--) {
								pos += parseFloat(begin[i1]) * factor;
								factor *= 60.0;
							}

							transcript.push({
								start: pos,
								text: n.textContent
							});
						}
					}

					this.setState({transcript});
				}
			});
		}
	}
}



// WEBPACK FOOTER //
// ./src/common/components/CompositeMultimedia.tsx